import { type RefObject, useEffect } from 'react';

function isFocusedNumberInput(target: EventTarget | null): target is HTMLInputElement {
  return target instanceof HTMLInputElement && target.type === 'number' && document.activeElement === target;
}

/**
 * Stops accidental changes on focused `input[type="number"]` inside the container:
 * - Mouse wheel stepping (e.g. while scrolling a long questionnaire)
 * - ArrowUp / ArrowDown stepping
 *
 * Wheel uses capture phase and `{ passive: false }` so `preventDefault` is honored.
 */
export function usePreventNumberInputScroll(containerRef: RefObject<HTMLElement | null>, enabled = true): void {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const el = containerRef.current;
    if (!el) {
      return;
    }

    const onWheel = (e: WheelEvent): void => {
      if (!isFocusedNumberInput(e.target)) {
        return;
      }
      e.preventDefault();
    };

    const onKeyDown = (e: KeyboardEvent): void => {
      if (!isFocusedNumberInput(e.target)) {
        return;
      }
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') {
        return;
      }
      e.preventDefault();
    };

    el.addEventListener('wheel', onWheel, { passive: false, capture: true });
    el.addEventListener('keydown', onKeyDown, { capture: true });
    return () => {
      el.removeEventListener('wheel', onWheel, { capture: true });
      el.removeEventListener('keydown', onKeyDown, { capture: true });
    };
  }, [enabled, containerRef]);
}
